/**
 * Insight Engine Components - Core Building Blocks
 * =================================================
 * Shared components for building narrative-first, trust-building dashboards
 */

import React from 'react';
import { 
  TrendingUp, TrendingDown, Minus, Clock, RefreshCw, 
  AlertTriangle, CheckCircle, Info, ExternalLink, ChevronDown, ChevronUp,
  Zap, Target, Award, ArrowRight, Calendar, Shield,
  Database
} from 'lucide-react';

// ============================================
// DATA SOURCE CONFIGURATION - Logos & Names
// Using local logos from /integrations/ folder
// ============================================
const DATA_SOURCE_CONFIG = {
  xero: { 
    name: 'Xero', 
    logo: 'https://upload.wikimedia.org/wikipedia/en/9/9f/Xero_software_logo.svg',
    color: '#13B5EA',
  },
  square: { 
    name: 'Square', 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/3/30/Square%2C_Inc._-_Square_logo.svg',
    color: '#006AFF',
  },
  quickbooks: { 
    name: 'QuickBooks', 
    logo: 'https://quickbooks.intuit.com/oidam/intuit/sbseg/en_us/Blog/Graphic/qb-logo-horizontal-preferred.png',
    color: '#2CA01C',
  },
  myob: { 
    name: 'MYOB', 
    logo: 'https://www.myob.com/content/dam/public-website/australia/images/logo/myob-logo.svg',
    color: '#6B21A8',
  },
  shopify: { 
    name: 'Shopify', 
    logo: 'https://cdn.shopify.com/shopifycloud/brochure/assets/brand-assets/shopify-logo-primary-logo-456baa801ee66a0a435671082365958316831c9960c480451dd0330bcdae304f.svg',
    color: '#96BF48',
  },
  google_analytics: { 
    name: 'Google Analytics', 
    logo: 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg',
    color: '#F9AB00',
  },
  meta_ads: { 
    name: 'Meta Ads', 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg',
    color: '#0668E1',
  },
  deputy: { 
    name: 'Deputy', 
    logo: '/integrations/deputy-official.png',
    color: '#00B2A9',
  },
  tanda: { 
    name: 'Tanda', 
    logo: null,
    color: '#00B2A9',
  },
  google_ads: { 
    name: 'Google Ads', 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Ads_logo.svg',
    color: '#4285F4',
  },
  benchmark: { 
    name: 'Industry Benchmark', 
    logo: null,
    color: '#F59E0B',
  },
  mailchimp: { 
    name: 'Mailchimp', 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/5/55/Mailchimp_Logo_Standard.svg',
    color: '#FFE01B',
  },
  hubspot: {
    name: 'HubSpot',
    logo: 'https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png',
    color: '#FF7A59',
  },
  lightspeed: {
    name: 'Lightspeed',
    logo: '/integrations/lightspeed-official.png',
    color: '#C4F022',
  },
  kounta: {
    name: 'Kounta',
    logo: '/integrations/kounta.png',
    color: '#FF6B00',
  },
  toast: {
    name: 'Toast POS',
    logo: '/integrations/toast.png',
    color: '#FF6600',
  },
  default: { 
    name: 'Data Source', 
    logo: null,
    color: '#6B7280',
  },
};

// ============================================
// DATA SOURCE BADGE - Compact Horizontal Layout
// ============================================
// Shows logo + name side by side (compact)

export const DataSourceBadge = ({ 
  source, 
  lastSync, 
  confidence = 100,
  showLastSync = false,
  showIcon = true,
  size = 'sm'
}) => {
  const sourceKey = source?.toLowerCase()?.replace(/\s+/g, '_') || 'default';
  const config = DATA_SOURCE_CONFIG[sourceKey] || DATA_SOURCE_CONFIG.default;

  const sizeClasses = {
    xs: { logo: 'h-3.5 w-3.5', name: 'text-[10px]', container: 'gap-1.5' },
    sm: { logo: 'h-4 w-4', name: 'text-xs', container: 'gap-1.5' },
    md: { logo: 'h-5 w-5', name: 'text-sm', container: 'gap-2' },
  };
  
  const currentSize = sizeClasses[size] || sizeClasses.sm;

  return (
    <div className={`inline-flex items-center ${currentSize.container}`}>
      {showIcon && config.logo ? (
        <img 
          src={config.logo} 
          alt={config.name}
          className={`${currentSize.logo} object-contain`}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : showIcon && !config.logo ? (
        <Database className={`${currentSize.logo} text-gray-400`} />
      ) : null}
      <span className={`font-medium text-gray-600 dark:text-gray-400 ${currentSize.name}`}>
        {config.name}
      </span>
    </div>
  );
};

// ============================================
// TRUSTED DATA SOURCES PANEL
// ============================================
// Multi-source badge for when data comes from multiple sources
export const MultiSourceBadge = ({ sources, showCount = true }) => {
  const [expanded, setExpanded] = React.useState(false);
  
  if (!sources || sources.length === 0) return null;
  if (sources.length === 1) {
    return <DataSourceBadge source={sources[0].name} lastSync={sources[0].lastSync} />;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-1.5 rounded-full border bg-violet-100 text-violet-700 border-violet-200 text-xs px-2 py-0.5 hover:bg-violet-200 transition-colors"
      >
        <span className="font-medium">{sources.length} Sources</span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      
      {expanded && (
        <div className="absolute top-full left-0 mt-1 z-10 bg-white rounded-lg shadow-lg border p-2 min-w-[200px]">
          <div className="flex flex-col gap-1.5">
            {sources.map((source, idx) => (
              <DataSourceBadge 
                key={idx}
                source={source.name} 
                lastSync={source.lastSync}
                confidence={source.confidence}
                size="xs"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================
// TREND INDICATOR
// ============================================
// Visual indicator for value changes over time

export const TrendIndicator = ({ 
  value, 
  previousValue, 
  period = 'week',
  format = 'percent',
  size = 'md',
  showValue = true,
  invertColors = false // For metrics where down is good (e.g., expenses)
}) => {
  const change = previousValue ? ((value - previousValue) / previousValue) * 100 : 0;
  const isPositive = change > 0;
  const isNeutral = Math.abs(change) < 0.5;
  
  let colorClass;
  if (isNeutral) {
    colorClass = 'text-gray-500 bg-gray-50';
  } else if (invertColors) {
    colorClass = isPositive ? 'text-red-600 bg-red-50' : 'text-emerald-600 bg-emerald-50';
  } else {
    colorClass = isPositive ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50';
  }

  const Icon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  const formatChange = () => {
    if (format === 'percent') return `${Math.abs(change).toFixed(1)}%`;
    if (format === 'currency') return `$${Math.abs(value - previousValue).toLocaleString()}`;
    return Math.abs(change).toFixed(1);
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${colorClass} ${sizeClasses[size]}`}>
      <Icon className="w-3.5 h-3.5" />
      {showValue && <span>{formatChange()}</span>}
      <span className="text-current/60 text-xs">vs last {period}</span>
    </span>
  );
};


// ============================================
// CONFIDENCE EXPLAINER
// ============================================
// "How we calculated this" panel

export const ConfidenceExplainer = ({
  title,
  sources,
  methodology,
  sampleSize,
  lastUpdated,
  isOpen = false,
}) => {
  const [expanded, setExpanded] = React.useState(isOpen);

  return (
    <div className="border-t border-dashed border-gray-200 mt-3 pt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors w-full"
      >
        <Info className="w-3.5 h-3.5" />
        <span>How we calculated this</span>
        {expanded ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
      </button>
      
      {expanded && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-2">
          {title && <div className="font-medium text-gray-700">{title}</div>}
          
          {sources && sources.length > 0 && (
            <div>
              <span className="font-medium text-gray-700">Data Sources: </span>
              {sources.join(', ')}
            </div>
          )}
          
          {methodology && (
            <div>
              <span className="font-medium text-gray-700">Methodology: </span>
              {methodology}
            </div>
          )}
          
          {sampleSize && (
            <div>
              <span className="font-medium text-gray-700">Based on: </span>
              {sampleSize.toLocaleString()} data points
            </div>
          )}
          
          {lastUpdated && (
            <div className="flex items-center gap-1 text-gray-500">
              <RefreshCw className="w-3 h-3" />
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ============================================
// INSIGHT CARD
// ============================================
// Main card component for individual insights

export const InsightCard = ({
  title,
  value,
  previousValue,
  trend,
  period = 'week',
  format = 'number',
  sources = [],
  children,
  methodology,
  sampleSize,
  icon: Icon,
  variant = 'default', // 'default', 'positive', 'negative', 'warning'
  onClick,
}) => {
  const variantStyles = {
    default: 'border-gray-200 bg-white',
    positive: 'border-emerald-200 bg-emerald-50/30',
    negative: 'border-red-200 bg-red-50/30',
    warning: 'border-amber-200 bg-amber-50/30',
  };

  const formatValue = (val) => {
    if (format === 'currency') return `$${val?.toLocaleString()}`;
    if (format === 'percent') return `${val?.toFixed(1)}%`;
    return val?.toLocaleString();
  };

  return (
    <div 
      className={`rounded-xl border p-4 ${variantStyles[variant]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        <MultiSourceBadge sources={sources} />
      </div>
      
      <div className="flex items-end justify-between">
        <div className="text-2xl font-bold text-gray-900">{formatValue(value)}</div>
        {previousValue !== undefined && (
          <TrendIndicator 
            value={value} 
            previousValue={previousValue} 
            period={period}
            format={format === 'currency' ? 'currency' : 'percent'}
          />
        )}
      </div>
      
      {children && <div className="mt-3">{children}</div>}
      
      {methodology && (
        <ConfidenceExplainer
          sources={sources?.map(s => s.name)}
          methodology={methodology}
          sampleSize={sampleSize}
        />
      )}
    </div>
  );
};


// ============================================
// WIN CARD - Premium Apple-Inspired Design
// ============================================
// For highlighting positive achievements with depth and motion

export const WinCard = ({
  title,
  description,
  metric,
  metricLabel,
  improvement,
  sources = [],
  timeframe = 'this week',
  icon: Icon = Award,
}) => {
  // Determine if metric is long (needs smaller text)
  const metricLength = metric?.toString().length || 0;
  const metricTextSize = metricLength > 4 ? 'text-sm' : 'text-lg';
  
  return (
    <div className="relative h-full overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50/90 via-white/95 to-blue-50/90 border border-emerald-200/40 p-5 shadow-[0_8px_30px_rgb(16,185,129,0.08),inset_0_0_0_1px_rgba(255,255,255,0.5)] backdrop-blur-xl transition-all duration-300 hover:shadow-[0_12px_40px_rgb(16,185,129,0.15)] hover:-translate-y-0.5 group">
      {/* Decorative gradient orb */}
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-emerald-200/40 to-[#4DA6FF]/30 rounded-full blur-2xl pointer-events-none group-hover:scale-110 transition-transform duration-500" />
      {/* Subtle shine effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative flex items-start gap-4">
        {/* Premium icon container - auto width for metrics */}
        <div className="min-w-[56px] h-14 px-3 rounded-2xl bg-gradient-to-br from-emerald-400 to-[#0066CC] flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30">
          {metric !== undefined ? (
            <span className={`${metricTextSize} font-bold text-white tracking-tight whitespace-nowrap`}>{metric}</span>
          ) : (
            <Icon className="w-6 h-6 text-white" />
          )}
        </div>
        
        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-start gap-3 mb-1.5 flex-wrap">
            <h4 className="font-semibold text-gray-900 text-base tracking-tight">{title}</h4>
            {improvement && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold text-emerald-700 bg-emerald-100 border border-emerald-200/50 whitespace-nowrap shadow-sm">
                <TrendingUp className="w-3 h-3" />
                {improvement}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">{description}</p>
          {metricLabel && (
            <span className="text-xs text-emerald-600/80 mt-1 block">{metricLabel}</span>
          )}
        </div>
      </div>
      
      {sources.length > 0 && (
        <div className="mt-4 pt-3 border-t border-emerald-100/50 flex items-center gap-2">
          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Sources:</span>
          <div className="flex items-center gap-2">
            {sources.slice(0, 3).map((source, idx) => (
              <DataSourceBadge key={idx} source={source} size="xs" showLastSync={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================
// OPPORTUNITY CARD - Premium Apple-Inspired Design
// ============================================
// For highlighting improvement areas with reasoning

export const OpportunityCard = ({
  title,
  description,
  reasoning,
  impact,
  impactLabel = 'Impact',
  priority = 'medium',
  suggestedActions = [],
  sources = [],
  schedule,
  likelihood,
  runway,
  bestChance,
  dataSource,
  benchmarkSource,
  onClick,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const priorityStyles = {
    low: { 
      gradient: 'from-blue-50/90 via-white/95 to-indigo-50/90',
      border: 'border-blue-200/40',
      iconBg: 'from-blue-400 to-indigo-500',
      iconShadow: 'shadow-blue-500/30',
      badge: 'bg-blue-100 text-blue-700 border-blue-200/50',
      impactText: 'text-blue-600',
      orb: 'from-blue-200/40 to-indigo-300/30',
      shadow: 'rgb(59,130,246,0.08)',
      hoverShadow: 'rgb(59,130,246,0.15)',
    },
    medium: { 
      gradient: 'from-amber-50/90 via-white/95 to-orange-50/90',
      border: 'border-amber-200/40',
      iconBg: 'from-amber-400 to-orange-500',
      iconShadow: 'shadow-amber-500/30',
      badge: 'bg-amber-100 text-amber-700 border-amber-200/50',
      impactText: 'text-amber-600',
      orb: 'from-amber-200/40 to-orange-300/30',
      shadow: 'rgb(245,158,11,0.08)',
      hoverShadow: 'rgb(245,158,11,0.15)',
    },
    high: { 
      gradient: 'from-rose-50/90 via-white/95 to-red-50/90',
      border: 'border-rose-200/40',
      iconBg: 'from-rose-400 to-red-500',
      iconShadow: 'shadow-rose-500/30',
      badge: 'bg-rose-100 text-rose-700 border-rose-200/50',
      impactText: 'text-rose-600',
      orb: 'from-rose-200/40 to-red-300/30',
      shadow: 'rgb(244,63,94,0.08)',
      hoverShadow: 'rgb(244,63,94,0.15)',
    },
  };

  const style = priorityStyles[priority];

  return (
    <div 
      className={`relative h-full overflow-hidden rounded-3xl bg-gradient-to-br ${style.gradient} border ${style.border} p-5 backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 group cursor-pointer`}
      style={{ 
        boxShadow: `0 8px 30px ${style.shadow}, inset 0 0 0 1px rgba(255,255,255,0.5)` 
      }}
      onClick={() => setExpanded(!expanded)}
      data-testid={`opportunity-card-${title?.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 12px 40px ${style.hoverShadow}, inset 0 0 0 1px rgba(255,255,255,0.6)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = `0 8px 30px ${style.shadow}, inset 0 0 0 1px rgba(255,255,255,0.5)`;
      }}
    >
      {/* Decorative gradient orb */}
      <div className={`absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br ${style.orb} rounded-full blur-2xl pointer-events-none group-hover:scale-110 transition-transform duration-500`} />
      {/* Subtle shine effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative flex items-start gap-4">
        {/* Premium icon container */}
        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${style.iconBg} flex items-center justify-center flex-shrink-0 shadow-lg ${style.iconShadow}`}>
          <Target className="w-6 h-6 text-white" />
        </div>
        
        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-start gap-2 mb-1.5">
            <h4 className="font-semibold text-gray-900 text-base tracking-tight flex-1 min-w-0">{title}</h4>
            <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
          {impact && (
            <span className={`inline-flex items-start gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${style.badge} shadow-sm mb-2 max-w-full`}>
              <Zap className="w-3 h-3 shrink-0 mt-0.5" />
              <span className="leading-tight break-words">{impact}</span>
            </span>
          )}
          <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
        </div>
      </div>
      
      {suggestedActions.length > 0 && (
        <div className="relative mt-4 flex flex-wrap gap-2">
          {suggestedActions.slice(0, expanded ? suggestedActions.length : 2).map((action, idx) => (
            <span key={idx} className="text-xs bg-white/70 backdrop-blur-sm px-3 py-1.5 rounded-full text-gray-600 border border-gray-200/50 shadow-sm">
              {idx + 1}. {action}
            </span>
          ))}
        </div>
      )}
      
      {sources.length > 0 && (
        <div className="relative mt-4 pt-3 border-t border-gray-100/50 flex items-center gap-2">
          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Sources:</span>
          <div className="flex items-center gap-2">
            {sources.slice(0, 3).map((source, idx) => (
              <DataSourceBadge key={idx} source={source} size="xs" showLastSync={false} />
            ))}
          </div>
        </div>
      )}

      {/* Expanded drill-down: Schedule, Runway, Likelihood */}
      {expanded && (
        <div className="relative mt-4 pt-4 border-t border-gray-200/50 space-y-4" data-testid="opportunity-detail">
          {reasoning && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Why This Matters</p>
              <p className="text-xs text-gray-700 leading-relaxed">{reasoning}</p>
            </div>
          )}

          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
            {likelihood && (
              <div className="bg-white/60 rounded-xl p-3 border border-gray-100">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Likelihood</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${likelihood >= 80 ? 'bg-emerald-500' : likelihood >= 60 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${likelihood}%` }} />
                  </div>
                  <span className={`text-sm font-bold ${likelihood >= 80 ? 'text-emerald-600' : likelihood >= 60 ? 'text-amber-600' : 'text-red-500'}`}>{likelihood}%</span>
                </div>
              </div>
            )}
            
            {runway && (
              <div className="bg-white/60 rounded-xl p-3 border border-gray-100">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Runway to Achieve</p>
                <p className="text-sm font-semibold text-gray-800">{runway}</p>
              </div>
            )}

            {schedule && (
              <div className="bg-white/60 rounded-xl p-3 border border-gray-100">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Suggested Schedule</p>
                <p className="text-sm font-semibold text-gray-800">{schedule}</p>
              </div>
            )}
          </div>

          {bestChance && (
            <div className="bg-emerald-50/80 rounded-xl p-3 border border-emerald-200/40">
              <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold mb-1">How to Maximise Success</p>
              <p className="text-xs text-emerald-700 leading-relaxed">{bestChance}</p>
            </div>
          )}

          {(dataSource || benchmarkSource) && (
            <div className="flex items-center gap-4 text-[10px] text-gray-400">
              {dataSource && <span>Data: {dataSource}</span>}
              {benchmarkSource && <span>Benchmark: {benchmarkSource}</span>}
            </div>
          )}
        </div>
      )}

      {!expanded && (
        <p className="relative text-[10px] text-gray-400 mt-3 italic">Click to see full implementation plan</p>
      )}
    </div>
  );
};


// ============================================
// INSIGHT SUMMARY CARD - Compact Version
// ============================================
// Personalized welcome card with narrative summary

export const InsightSummaryCard = ({
  userName,
  lastLogin,
  keyChanges = [],
  overallTrend = 'stable',
  sources = [],
  businessName,
}) => {
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const formatLastLogin = () => {
    if (!lastLogin) return 'first time here';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'earlier today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const trendColors = {
    improving: 'text-emerald-400',
    stable: 'text-blue-400',
    declining: 'text-amber-400',
  };

  const trendLabels = {
    improving: 'Things are looking up!',
    stable: 'Steady as she goes.',
    declining: 'Some areas need attention.',
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5">
      <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-24 translate-x-24" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-16 -translate-x-16" />
      
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-xl font-semibold">
              {getGreeting()}, {userName?.split(' ')[0] || 'there'}
            </h2>
            <p className="text-sm text-slate-400">
              {businessName && <span>{businessName} • </span>}
              Last visit: {formatLastLogin()}
            </p>
          </div>
          {sources.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Data from:</span>
              <div className="flex items-center gap-1.5">
                {sources.slice(0, 4).map((source, idx) => {
                  const sourceKey = source?.toLowerCase()?.replace(/\s+/g, '_') || 'default';
                  const config = DATA_SOURCE_CONFIG[sourceKey] || DATA_SOURCE_CONFIG.default;
                  return config.logo ? (
                    <img 
                      key={idx}
                      src={config.logo} 
                      alt={config.name}
                      title={config.name}
                      className="h-4 w-4 object-contain opacity-70 hover:opacity-100 transition-opacity"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
        
        <div className={`text-lg font-medium ${trendColors[overallTrend]} mb-3`}>
          {trendLabels[overallTrend]}
        </div>
        
        {keyChanges.length > 0 && (
          <div className="space-y-1.5">
            {keyChanges.slice(0, 3).map((change, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                {change.trend === 'up' && <TrendingUp className="w-4 h-4 text-emerald-400" />}
                {change.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-400" />}
                {change.trend === 'neutral' && <Minus className="w-4 h-4 text-slate-400" />}
                <span className="text-slate-300">{change.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================
// DATA QUALITY INDICATOR
// ============================================
// Shows overall data confidence

export const DataQualityIndicator = ({
  score,
  sources = [],
  lastFullSync,
  issues = 0,
}) => {
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-600 bg-emerald-100';
    if (score >= 70) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    return 'Needs attention';
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${getScoreColor(score)}`}>
        {score}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">Data Quality: {getScoreLabel(score)}</span>
          {issues > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              {issues} issue{issues !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <span>{sources.length} connected sources</span>
          {lastFullSync && (
            <>
              <span>•</span>
              <span>Last sync: {new Date(lastFullSync).toLocaleString()}</span>
            </>
          )}
        </div>
      </div>
      <CheckCircle className={`w-5 h-5 ${score >= 70 ? 'text-emerald-500' : 'text-amber-500'}`} />
    </div>
  );
};


// ============================================
// ANOMALY ALERT
// ============================================
// For flagging data discrepancies

export const AnomalyAlert = ({
  title,
  description,
  severity = 'medium', // 'low', 'medium', 'high', 'critical'
  affectedSources = [],
  onInvestigate,
  onDismiss,
}) => {
  const severityStyles = {
    low: { bg: 'bg-blue-50', border: 'border-blue-200', icon: Info, iconColor: 'text-blue-500' },
    medium: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500' },
    high: { bg: 'bg-orange-50', border: 'border-orange-200', icon: AlertTriangle, iconColor: 'text-orange-500' },
    critical: { bg: 'bg-red-50', border: 'border-red-200', icon: AlertTriangle, iconColor: 'text-red-500' },
  };

  const style = severityStyles[severity];
  const Icon = style.icon;

  return (
    <div className={`rounded-lg border ${style.border} ${style.bg} p-4`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${style.iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{title}</h4>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
          
          {affectedSources.length > 0 && (
            <div className="flex gap-1 mt-2">
              {affectedSources.map((source, idx) => (
                <DataSourceBadge key={idx} source={source} size="xs" showLastSync={false} />
              ))}
            </div>
          )}
          
          <div className="flex gap-2 mt-3">
            {onInvestigate && (
              <button
                onClick={onInvestigate}
                className="text-xs font-medium px-3 py-1.5 rounded bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Investigate
              </button>
            )}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-xs text-gray-500 px-3 py-1.5 hover:text-gray-700 transition-colors"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default {
  DataSourceBadge,
  MultiSourceBadge,
  TrendIndicator,
  ConfidenceExplainer,
  InsightCard,
  WinCard,
  OpportunityCard,
  InsightSummaryCard,
  DataQualityIndicator,
  AnomalyAlert,
};
